import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '../../components/ui/command';
import { ArrowLeft, Loader2, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';

export default function CreateBatch() {
  const navigate = useNavigate();
  const [instructors, setInstructors] = useState([]);
  const [learners, setLearners] = useState([]);
  const [learnerOpen, setLearnerOpen] = useState(false);
  const [learnerSearch, setLearnerSearch] = useState('');
  const [learnerLoading, setLearnerLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', batch_type: 'one_on_one', course_type: 'prenatal',
    instructor_id: '', learner_id: '', credits_total: 12,
    timezone: 'Asia/Kolkata', share_recordings: false, allow_learner_cancel: true,
  });

  useEffect(() => {
    // Instructor + superadmin can be assigned to a batch — admins cannot.
    Promise.all([
      api.get('/api/users', { params: { role: 'instructor', limit: 100 } }),
      api.get('/api/users', { params: { role: 'admin', limit: 20 } }),
    ]).then(([instRes, adminRes]) => {
      const superadmins = (adminRes.data.users || []).filter(u => u.role === 'superadmin');
      const all = [...(instRes.data.users || []), ...superadmins];
      setInstructors(all.filter((u, i, arr) => arr.findIndex(a => a.id === u.id) === i));
    });
    api.get('/api/users', { params: { role: 'learner', limit: 100 } }).then(res => setLearners(res.data.users));
  }, []);

  // Server-side learner search (debounced) — supports the full 1000+ learner base,
  // not just the first 100. Selected learner is kept in `learners` so its label persists.
  useEffect(() => {
    const t = setTimeout(() => {
      setLearnerLoading(true);
      api.get('/api/users', { params: { role: 'learner', search: learnerSearch, limit: 50 } })
        .then(res => {
          const fetched = res.data.users || [];
          setLearners(prev => {
            const selected = prev.find(l => l.id === form.learner_id);
            if (selected && !fetched.some(l => l.id === selected.id)) return [selected, ...fetched];
            return fetched;
          });
        })
        .catch(() => {})
        .finally(() => setLearnerLoading(false));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnerSearch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Batch name is required'); return; }
    // For 1:1 batches, instructor is required at creation time (the batch
    // is the relationship). For group batches, instructor is optional —
    // admins assign per-session at scheduling time.
    if (form.batch_type === 'one_on_one' && !form.instructor_id) {
      toast.error('Please select an instructor for this 1:1 batch');
      return;
    }
    setLoading(true);
    try {
      // Strip empty instructor_id so backend treats it as unset (group flow).
      const payload = { ...form };
      if (!payload.instructor_id) delete payload.instructor_id;
      const res = await api.post('/api/batches', payload);
      toast.success('Batch created!');
      navigate(`/admin/batches/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create batch');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/batches')}><ArrowLeft className="h-4 w-4" /></Button>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold">Create Batch</h1>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Batch Name</label>
              <Input placeholder="e.g. MWF Prenatal Yoga - IST" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="batch-name-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Batch Type</label>
                <Select value={form.batch_type} onValueChange={v => setForm({...form, batch_type: v})}>
                  <SelectTrigger data-testid="batch-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_on_one">1:1 Session</SelectItem>
                    <SelectItem value="group">Group Session</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Course Type</label>
                <Select value={form.course_type} onValueChange={v => setForm({...form, course_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prenatal">Prenatal</SelectItem>
                    <SelectItem value="postnatal">Postnatal</SelectItem>
                    <SelectItem value="ttc">TTC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Instructor — required for 1:1 (the batch IS the relationship);
                optional for group batches (admin assigns per-session at
                scheduling time, per ScheduleSessions.js logic). */}
            {form.batch_type === 'one_on_one' && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Instructor</label>
                <Select value={form.instructor_id} onValueChange={v => setForm({...form, instructor_id: v})}>
                  <SelectTrigger data-testid="instructor-select"><SelectValue placeholder="Select instructor" /></SelectTrigger>
                  <SelectContent>
                    {instructors.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.batch_type === 'one_on_one' && (
              <>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Learner</label>
                  <Popover open={learnerOpen} onOpenChange={setLearnerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={learnerOpen}
                        className="w-full justify-between font-normal"
                        data-testid="learner-select-trigger"
                      >
                        {(() => {
                          const sel = learners.find(l => l.id === form.learner_id);
                          return sel
                            ? <span className="truncate">{sel.name} ({sel.email})</span>
                            : <span className="text-muted-foreground">Select learner</span>;
                        })()}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Search by name, email or phone..."
                          value={learnerSearch}
                          onValueChange={setLearnerSearch}
                          data-testid="learner-search-input"
                        />
                        <CommandList>
                          <CommandEmpty>
                            {learnerLoading ? 'Searching…' : 'No learner found.'}
                          </CommandEmpty>
                          {learners.map(l => (
                            <CommandItem
                              key={l.id}
                              value={l.id}
                              onSelect={() => {
                                setForm({ ...form, learner_id: l.id });
                                setLearnerOpen(false);
                              }}
                              data-testid={`learner-option-${l.id}`}
                            >
                              <Check className={`mr-2 h-4 w-4 ${form.learner_id === l.id ? 'opacity-100' : 'opacity-0'}`} />
                              <span className="truncate">{l.name} ({l.email})</span>
                            </CommandItem>
                          ))}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Total Credits</label>
                  <Input type="number" min={0} value={form.credits_total} onChange={e => setForm({...form, credits_total: parseInt(e.target.value) || 0})} />
                </div>
              </>
            )}

            <div>
              <label className="text-sm font-medium mb-1.5 block">Timezone</label>
              <Select value={form.timezone} onValueChange={v => setForm({...form, timezone: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Asia/Kolkata">Asia/Kolkata (IST +05:30)</SelectItem>
                  <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
                  <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST)</SelectItem>
                  <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                  <SelectItem value="America/Chicago">America/Chicago (CST)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Share Recordings</span>
                <Switch checked={form.share_recordings} onCheckedChange={v => setForm({...form, share_recordings: v})} />
              </div>
              {form.batch_type === 'one_on_one' && (
                <div className="flex items-center justify-between">
                  <span className="text-sm">Allow Learner Cancellation</span>
                  <Switch checked={form.allow_learner_cancel} onCheckedChange={v => setForm({...form, allow_learner_cancel: v})} />
                </div>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={loading} data-testid="create-batch-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Batch
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
